import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { LoadingState } from '../../types';
import benefitService, { Benefit } from '../../services/benefitService';

interface BenefitState extends LoadingState {
  benefits: Benefit[];
  selectedBenefit: Benefit | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    hasMore: boolean;
  };
  searchQuery: string;
}

const initialState: BenefitState = {
  benefits: [],
  selectedBenefit: null,
  pagination: {
    page: 1,
    limit: 5,
    total: 0,
    pages: 0,
    hasMore: false,
  },
  searchQuery: '',
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchBenefits = createAsyncThunk(
  'benefits/fetchBenefits',
  async (params: { page?: number; limit?: number; search?: string; append?: boolean } = {}, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 5, search = '', append = false } = params;
      const response = await benefitService.getActiveBenefits({ page, limit });
      return { ...response, append };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch benefits');
    }
  }
);

export const createBenefit = createAsyncThunk(
  'benefits/createBenefit',
  async (data: { name: string; status: 'active' | 'inactive' }, { rejectWithValue }) => {
    try {
      const response = await benefitService.createBenefit(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create benefit');
    }
  }
);

const benefitSlice = createSlice({
  name: 'benefits',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedBenefit: (state, action: PayloadAction<Benefit | null>) => {
      state.selectedBenefit = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
      state.benefits = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Benefits
      .addCase(fetchBenefits.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBenefits.fulfilled, (state, action) => {
        state.isLoading = false;
        const { data, append } = action.payload;
        
        if (append) {
          state.benefits.push(...data.data as any);
        } else {
          state.benefits = data.data as any;
        }
        
        state.pagination = {
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          pages: data.pagination.pages || data.pagination.totalPages,
          hasMore: data.pagination.page < (data.pagination.pages || data.pagination.totalPages),
        };
      })
      .addCase(fetchBenefits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Benefit
      .addCase(createBenefit.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createBenefit.fulfilled, (state, action) => {
        state.isLoading = false;
        const newBenefit = action.payload;
        
        // Create a properly typed object for WritableDraft
        const benefitToAdd: Benefit = {
          _id: newBenefit._id,
          name: newBenefit.name,
          slug: newBenefit.slug,
          status: newBenefit.status,
          isDeleted: newBenefit.isDeleted,
          createdBy: newBenefit.createdBy,
          createdAt: newBenefit.createdAt,
          updatedAt: newBenefit.updatedAt
        };
        
        state.benefits.unshift(benefitToAdd as any);
        state.pagination.total += 1;
      })
      .addCase(createBenefit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedBenefit,
  setSearchQuery,
  resetPagination,
} = benefitSlice.actions;

export default benefitSlice.reducer;
